import { Link } from "react-router-dom";
import { TopBar } from "../components/Layout";

/**
 * Full technical overview of the ActionConnect platform. Authored content
 * (source of truth: repo ARCHITECTURE.md) rendered as a staff-facing document.
 */
export function ResourceOverviewPage() {
  return (
    <>
      <TopBar crumbs={<><Link to="/resources">Resources</Link> / Full technical overview</>} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Full technical overview</h1>
            <div className="sub">
              How ActionConnect is built end to end — surfaces, routing, isolation, data, provisioning,
              compute, and security. Source of truth: <code>ARCHITECTURE.md</code>.
            </div>
          </div>
        </div>

        <article className="doc card">
          <div className="card-body">
            <h2 id="what">What it is</h2>
            <p>
              ActionConnect is a multi-tenant SaaS that combines a <strong>helpdesk</strong> with a{" "}
              <strong>work-management suite</strong> (the best of Jira + Asana + Monday), built entirely on{" "}
              <strong>Cloudflare</strong> — Workers, Pages, D1, R2, KV, Durable Objects, Queues, and Email
              Routing. Each customer is an <strong>organization (tenant)</strong> provisioned with isolated
              resources at signup, using a <strong>database-per-tenant</strong> isolation model.
            </p>
            <p className="muted">
              Everything is <code>ac</code>-prefixed: packages <code>@ac/*</code>, Workers <code>ac-*</code>,
              and per-tenant resources (D1 <code>ac-tenant-&lt;ulid&gt;</code>, KV <code>ac-routing</code>,
              R2 <code>ac-tenant-&lt;ulid&gt;</code>). TypeScript throughout; app-generated ULIDs; unix-second
              timestamps; D1 tables are <code>STRICT</code> with foreign keys on.
            </p>

            <h2 id="surfaces">The five surfaces</h2>
            <p>Five separately-routed front-ends, each with its own host and auth model:</p>
            <table className="data">
              <thead>
                <tr>
                  <th>Surface</th>
                  <th>Host</th>
                  <th>Tenant-scoped?</th>
                  <th>Auth</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Marketing</strong></td>
                  <td className="mono">actionconnect.com</td>
                  <td>No</td>
                  <td>Public + signup</td>
                </tr>
                <tr>
                  <td><strong>Tenant app (agents)</strong></td>
                  <td className="mono">{"{org}"}.actionconnect.com</td>
                  <td>Yes</td>
                  <td>Session (password / SSO)</td>
                </tr>
                <tr>
                  <td><strong>Customer portal</strong></td>
                  <td className="mono">subdomain or custom domain</td>
                  <td>Yes</td>
                  <td>Magic-link (hashed tokens)</td>
                </tr>
                <tr>
                  <td><strong>Platform admin console</strong></td>
                  <td className="mono">admin.actionconnect.com</td>
                  <td>No (control-plane)</td>
                  <td>SSO + admin role gate</td>
                </tr>
                <tr>
                  <td><strong>Docs / user manual</strong></td>
                  <td className="mono">docs.actionconnect.com</td>
                  <td>No</td>
                  <td>Public</td>
                </tr>
              </tbody>
            </table>
            <p className="muted">
              This console is surface #4 — staff-only control-plane tooling. The tenant&apos;s own
              admin/settings area lives inside the tenant app (#2), not here.
            </p>

            <h2 id="routing">Request routing &amp; tenancy</h2>
            <p>
              A single entry Worker (<code>ac-edge</code>) fronts the wildcard <code>*.actionconnect.com</code>{" "}
              plus registered custom domains. It classifies every request into a surface and, for tenant
              surfaces, resolves the tenant <em>before</em> any business logic runs:
            </p>
            <pre className="doc-pre">{`Request host ──► ac-edge (router)
   ├─ apex / www                  ──► Pages: ac-marketing      [no tenant]
   ├─ docs.actionconnect.com      ──► Pages: ac-docs           [no tenant]
   ├─ admin.actionconnect.com     ──► ac-admin (control-plane) [no tenant]
   ├─ {org}.actionconnect.com     ──► ac-tenant-app            [TENANT]
   └─ custom domain (portal/app)  ──► KV lookup ──► ac-portal  [TENANT]`}</pre>
            <p>
              The <code>ac-routing</code> KV maps an incoming host → tenant record, and a tenant id → its
              resource handles (D1 database id, R2 bucket, settings prefix, status, plan, custom domains).
              Resolution path: <code>host → tenant_id → resource handles</code>. Settings and feature flags
              are cached under <code>settings:{"{tenant_id}"}:*</code> for hot reads.
            </p>

            <h3 id="isolation">Isolation is structural</h3>
            <p>
              A Hono middleware (<code>withTenant</code>) runs on every tenant-surface request: it resolves
              the host to a <code>tenant_id</code> (404 + audit if unknown/suspended), loads that tenant&apos;s
              handles, and attaches a <strong>TenantContext</strong> exposing <em>only</em> that tenant&apos;s
              D1, R2, and settings. All data access goes through TenantContext — handlers never receive a raw
              global DB, and there is <strong>no API that takes a <code>tenant_id</code> to “switch” tenants</strong>.
              The tenant is fixed by the request host, which makes cross-tenant reads/writes structurally
              impossible rather than merely access-controlled. This is proven by isolation tests: a request to
              tenant A&apos;s host can never observe tenant B&apos;s rows, even with forged ids in the body.
            </p>

            <h2 id="data">Data architecture</h2>
            <ul>
              <li>
                <strong>One D1 database per tenant</strong> (<code>ac-tenant-&lt;ulid&gt;</code>), created at
                provisioning and migrated independently. No tenant&apos;s queries can reach another&apos;s bytes.
              </li>
              <li>
                A <strong>shared control-plane D1</strong> (<code>ac-control-plane</code>) holds the tenant
                registry, plans, subscriptions, provisioning state/audit, contracts, and platform staff/auth.
              </li>
              <li>
                <strong>R2:</strong> a bucket/prefix per tenant for attachments and raw inbound MIME.{" "}
                <strong>KV:</strong> <code>ac-routing</code> (host→tenant→resources) and cached per-tenant settings.
              </li>
            </ul>
            <p className="muted">
              Trade-off (documented): D1 has per-account database-count and per-database size ceilings.
              Database-per-tenant is the deliberate choice for hard isolation; the registry tracks every DB and
              carries <code>account_ref</code>/<code>region</code> columns so sharding across Cloudflare accounts
              is a config change, not a rewrite.
            </p>
            <p>
              Per-tenant schema is versioned, ordered SQL in <code>packages/ac-tenant-db/migrations/</code> with a{" "}
              <code>schema_migrations</code> ledger inside each tenant DB — covering identity/RBAC, tickets,
              projects/boards/tasks, customization + portal + email, work management (epics/sprints/automation/
              dashboards), cross-module integration + reports, SSO, and directory sync.
            </p>

            <h2 id="compute">Compute, queues &amp; real-time</h2>
            <h3>Workers (Hono APIs)</h3>
            <table className="data">
              <thead>
                <tr><th>Worker</th><th>Role</th></tr>
              </thead>
              <tbody>
                <tr><td className="mono">ac-edge</td><td>Host router + tenant resolution + custom-domain dispatch</td></tr>
                <tr><td className="mono">ac-tenant-app</td><td>Helpdesk + work-management API (tenant-scoped)</td></tr>
                <tr><td className="mono">ac-portal</td><td>Customer portal API (tenant-scoped, magic-link)</td></tr>
                <tr><td className="mono">ac-admin</td><td>Platform control-plane API (staff-only)</td></tr>
                <tr><td className="mono">ac-provisioner</td><td>Queue consumer: provisioning/de-provisioning state machine + CF REST</td></tr>
                <tr><td className="mono">ac-email-in</td><td>Email Routing Worker: parse MIME → R2 → thread → ticket</td></tr>
                <tr><td className="mono">ac-email-out</td><td>Queue consumer: render template → Mailgun → write back status</td></tr>
                <tr><td className="mono">ac-notifications</td><td>Queue consumer: fan-out of in-app + email notifications</td></tr>
              </tbody>
            </table>
            <h3>Queues &amp; Durable Objects</h3>
            <p>
              Queues keep slow work off the request path: <code>ac-provisioning</code>,{" "}
              <code>ac-email-out</code>, and <code>ac-notifications</code> (each with a dead-letter queue).
              Two Durable Object classes coordinate real-time, order-sensitive work and persist deltas back to
              the tenant D1 (so a cold DO rebuilds from D1):
            </p>
            <ul>
              <li>
                <strong>AcBoardRoom</strong> (one per board) — authoritative coordinator for live drag-drop
                ordering and presence over WebSockets; serializes concurrent moves without lost updates.
              </li>
              <li>
                <strong>AcTicketStream</strong> (one per ticket/inbox) — live comment/activity feed and agent
                presence (“who&apos;s viewing”).
              </li>
            </ul>
            <p className="muted">
              Each DO is keyed by <code>{"{tenant_id}:{board_id|ticket_id}"}</code> so it can never mix tenants.
            </p>

            <h3 id="email">Email</h3>
            <p>
              <strong>Inbound:</strong> Cloudflare Email Routing → <code>ac-email-in</code> resolves the tenant by
              destination address, stores raw MIME in the tenant&apos;s R2, and threads via{" "}
              <code>Message-ID</code>/<code>In-Reply-To</code>/<code>References</code> — a new thread opens a
              ticket, a reply appends a comment. <strong>Outbound:</strong> <code>ac-email-out</code> renders the
              matching template, sends via <strong>Mailgun</strong> (behind a pluggable{" "}
              <code>EmailProvider</code>), and writes back <code>sent/failed/bounced</code> + provider id,
              honoring per-user notification preferences.
            </p>

            <h2 id="provisioning">Provisioning lifecycle</h2>
            <p>
              Triggered by marketing signup or this admin console. Both write a control-plane <code>tenants</code>{" "}
              row (<code>status=provisioning</code>) plus a <code>provisioning_jobs</code> row, then enqueue to{" "}
              <code>ac-provisioning</code>; signup returns immediately and the client polls. The provisioner runs
              an <strong>idempotent, resumable step state machine</strong> recorded in{" "}
              <code>provisioning_steps</code>, each guarded by an idempotency key:
            </p>
            <table className="data">
              <thead>
                <tr><th>#</th><th>Step</th><th>Idempotent because…</th></tr>
              </thead>
              <tbody>
                <tr><td className="num">1</td><td className="mono">registry</td><td>upsert tenant row by id</td></tr>
                <tr><td className="num">2</td><td className="mono">create_d1</td><td>check registry before create; CF create keyed by name</td></tr>
                <tr><td className="num">3</td><td className="mono">migrate</td><td>tracked in the tenant DB&apos;s <code>schema_migrations</code></td></tr>
                <tr><td className="num">4</td><td className="mono">r2</td><td>bucket name derived from tenant id; create-if-absent</td></tr>
                <tr><td className="num">5</td><td className="mono">kv</td><td>routing/settings keys are deterministic upserts</td></tr>
                <tr><td className="num">6</td><td className="mono">routing</td><td>subdomain + inbound address; custom-domain path</td></tr>
                <tr><td className="num">7</td><td className="mono">seed</td><td>seed rows keyed by stable codes; insert-or-ignore</td></tr>
                <tr><td className="num">8</td><td className="mono">activate</td><td>set <code>status=active</code>, publish KV <code>host:</code> keys</td></tr>
              </tbody>
            </table>
            <p>
              On partial failure the job resumes from the first non-<code>done</code> step (no duplicate
              resources); every transition is audited. De-provisioning reverses the teardown (revoke routing/KV,
              delete R2 + D1, mark <code>deleted</code>) and is equally step-tracked and idempotent. New tenants
              always get the latest migration set; rolling out a new migration to existing tenants is a
              control-plane <strong>backfill job</strong> that pages the registry and applies pending migrations
              per tenant.
            </p>

            <h2 id="security">Security &amp; guardrails</h2>
            <ul>
              <li><strong>Isolation</strong> — structural (no tenant-switch API), proven by tests.</li>
              <li><strong>Secrets</strong> — all Cloudflare API tokens and Mailgun keys are Worker secrets, never in code or wrangler files; <code>.dev.vars</code> (gitignored) for local only.</li>
              <li><strong>Admin</strong> — SSO + admin-role gate; every admin action audited; <strong>impersonation is time-boxed</strong> (<code>impersonation_grants.expires_at</code>) and logged on use.</li>
              <li><strong>Portal auth</strong> — magic-link tokens are SHA-256-hashed into <code>portal_sessions</code>; the raw token exists only in the emailed link.</li>
              <li><strong>Automations</strong> — run async, scoped to one tenant&apos;s context, with loop/recursion guards (max depth + per-item budget + cycle dedupe).</li>
              <li><strong>Logging</strong> — single-line JSON with recursive secret redaction (password/token/secret/authorization/api-key/cookie → <code>[redacted]</code>).</li>
            </ul>

            <h2 id="commercial">Commercial flow: trials, contracts &amp; e-sign</h2>
            <p>
              Billing is <strong>manual / staff-billed</strong> (no payment gateway). New tenants get a{" "}
              <strong>90-day trial</strong>. Conversion is contract-driven, modeled entirely in the control
              plane: staff <strong>issue</strong> a contract (the body is snapshotted and bound by{" "}
              <code>document_hash = SHA-256(body)</code>); signers <strong>sign online</strong> (typed or drawn,
              ESIGN/UETA-style consent) via tokenized links whose hash only is stored; every{" "}
              <code>created/sent/viewed/signed/declined/completed</code> transition is an immutable{" "}
              <code>contract_events</code> row with IP + user-agent; then staff <strong>activate</strong> the paid
              subscription. A <code>BillingProvider</code> seam is left for a future gateway.
            </p>

            <h2 id="identity">Tenant SSO &amp; directory sync</h2>
            <p>
              <strong>SSO</strong> lets each tenant connect its own IdP (OIDC/SAML, incl. Google/Microsoft) so
              agents sign in via SSO with JIT provisioning — client secrets are Worker secrets, never stored in
              the DB; the OIDC callback verifies the id_token against the IdP&apos;s JWKS (RS256 only).{" "}
              <strong>Directory sync</strong> proactively imports/reconciles users from Azure Entra ID via
              app-only Microsoft Graph; the stored client secret is envelope-encrypted (AES-256-GCM) with a
              Worker-held KEK (<code>DIRECTORY_KEK</code>), and sync matches by Graph object id, JIT-creates,
              updates, and optionally deactivates vanished users — idempotent and re-runnable.
            </p>

            <h2 id="services">Cross-cutting &amp; operations services</h2>
            <p>Tenant-scoped facades on <code>@ac/tenant-db</code>, isolated like everything else:</p>
            <ul>
              <li><strong>Auth &amp; sessions</strong> — email+password with PBKDF2/WebCrypto; opaque server sessions (only the token hash is stored).</li>
              <li><strong>Notifications</strong> — in-app + email honoring per-user preferences, via the outbound outbox.</li>
              <li><strong>SLA engine</strong> — business-hours-aware first-response/resolution timers + breach sweep.</li>
              <li><strong>Search</strong> — unified, wildcard-safe search across tickets/tasks/KB/contacts.</li>
              <li><strong>Reports</strong> — runnable, exportable, schedulable aggregations across helpdesk + work (e.g. SLA compliance, cross-module workload).</li>
              <li><strong>Scheduled maintenance</strong> (<code>ac-cron</code>, hourly) — SLA breach sweep, session cleanup, scheduled report digests.</li>
              <li><strong>Export + GDPR</strong> — full JSON backup (secrets stripped), data-subject access export, and right-to-erasure.</li>
              <li><strong>Control-plane health</strong> — powers this console&apos;s provisioning status: job/tenant counts, recent failures, stuck-in-provisioning detection, trials expiring soon.</li>
            </ul>

            <h2 id="repo">Repository layout</h2>
            <pre className="doc-pre">{`System/
├─ packages/   ac-shared · ac-control-plane-db · ac-tenant-db
│              ac-tenant-context · ac-provisioning · ac-email · ac-directory
├─ workers/    ac-edge · ac-tenant-app · ac-portal · ac-admin
│              ac-provisioner · ac-email-in · ac-email-out · ac-notifications
├─ apps/       ac-marketing · ac-app-web · ac-portal-web · ac-admin-web · ac-docs
├─ scripts/    seed/local provisioning
└─ tests/      isolation + provisioning integration`}</pre>

            <p className="muted" style={{ marginBottom: 0 }}>
              For the authoritative, always-current version (including phase-by-phase build status), see{" "}
              <code>ARCHITECTURE.md</code> at the repository root.
            </p>
          </div>
        </article>
      </div>
    </>
  );
}
